import { useMemo } from 'react';
import { Box, Paper, TextField, Typography } from '@mui/material';
import type { RockBTSGeneral, RockBTSSpecimen } from '../../../types/rockBts';
import { computeBtsSpecimenPreview } from './computations';
// Shared section idiom (reused read-only from the Rock TCS module)
import { inputSx, paperSx, SectionHeader } from '../rock-tcs/sectionStyles';
import { SpecimenResultCard } from './sections/SpecimenResultCard';
import { SummaryOfResults } from './sections/SummaryOfResults';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';

interface Props {
  general: RockBTSGeneral;
  specimens: RockBTSSpecimen[];
  onGeneralChange: (field: keyof RockBTSGeneral, value: RockBTSGeneral[keyof RockBTSGeneral]) => void;
  onSpecimenChange: (
    specimenNumber: number,
    field: keyof RockBTSSpecimen,
    value: RockBTSSpecimen[keyof RockBTSSpecimen],
  ) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

/**
 * Tab 2 — Result Entry (mockup: Rock BTS Result Entry.png):
 * per-specimen loading + failure cards, the static reference panel, the
 * Summary of Results strip, and the shared report remarks (appended to the
 * LABORATORY COMMENTS after the failure sentences).
 */
export const RockBTSResultEntryTab = ({ general, specimens, onGeneralChange, onSpecimenChange, readOnly, workOrderDetailKey }: Props) => {
  const included = useMemo(
    () => specimens.filter((s) => s.specimenNumber <= general.specimenCount),
    [specimens, general.specimenCount],
  );
  const previews = useMemo(() => included.map((s) => computeBtsSpecimenPreview(s)), [included]);

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
        Number of Specimens: {general.specimenCount} — enter results for{' '}
        {general.specimenCount === 2 ? 'specimen 1 and specimen 2' : 'the specimen'}. Change the count on Test Parameters.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          // BUG-2026-0133 — the right-hand ReferencePanel is gone; its figures now
          // live inline in each specimen card as click-to-open popups. Cards fill
          // the row: two equal columns for 2 specimens, one full-width for 1.
          gridTemplateColumns: {
            xs: '1fr',
            lg: general.specimenCount === 2 ? '1fr 1fr' : '1fr',
          },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        {included.map((s, i) => (
          <SpecimenResultCard
            key={s.specimenNumber}
            specimen={s}
            preview={previews[i]}
            title={`Specimen ${s.specimenNumber}`}
            onChange={(field, value) => onSpecimenChange(s.specimenNumber, field, value)}
            readOnly={readOnly}
          />
        ))}
      </Box>

      <SummaryOfResults specimens={included} previews={previews} specimenCount={general.specimenCount} />

      <Paper variant="outlined" sx={paperSx}>
        <SectionHeader label="Report Remarks (appended to Laboratory Comments)" />
        <TextField
          size="small" fullWidth multiline minRows={2}
          placeholder="Optional remarks printed after the failure description(s) in the report's LABORATORY COMMENTS box."
          value={general.remarksText ?? ''}
          onChange={(e) => onGeneralChange('remarksText', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        />
      </Paper>

      {/* ── Internal media evidence — never printed ─────────────────────── */}
      {workOrderDetailKey && (
        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="Additional Observations (internal — not printed on the report)" />
          <TextField
            size="small" fullWidth multiline minRows={3}
            placeholder="Internal observations about specimens, test conditions, or equipment — for the lab and reviewer only."
            value={general.additionalObservations ?? ''}
            onChange={(e) => onGeneralChange('additionalObservations', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
          <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
        </Paper>
      )}
    </Box>
  );
};
